import { v4 as uuidv4 } from 'uuid';

export const attemptFingerprint = (fields: readonly unknown[]): string => JSON.stringify(fields);

/**
 * One-time keys for writes the server de-duplicates by key. A key belongs to the attempt's fingerprint
 * (what the operator asked to write), so a retry of the same ask reuses it and the server answers with the
 * first result instead of writing twice; a changed ask mints a new key. A key is dropped only once the write
 * is known to have landed — a failed or cut request may still have reached the server.
 */
export class AttemptKeys {
  private readonly keys = new Map<string, string>();

  /** The key for this ask: the one already minted for an unfinished attempt, or a fresh one. */
  public keyFor(fingerprint: string): string {
    let key = this.keys.get(fingerprint);
    if (key === undefined) {
      key = uuidv4();
      this.keys.set(fingerprint, key);
    }
    return key;
  }

  /** The write landed; the next identical ask is a new attempt. */
  public release(fingerprint: string): void {
    this.keys.delete(fingerprint);
  }

  /** Runs one write under its key, dropping the key once it resolves. A rejection keeps it for the retry. */
  public async run<T>(fingerprint: string, write: (idempotencyKey: string) => Promise<T>): Promise<T> {
    const result = await write(this.keyFor(fingerprint));
    this.release(fingerprint);
    return result;
  }
}
